import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Result, Button, Typography, Spin, Card, Descriptions } from 'antd';
import { CheckCircleFilled, FileTextOutlined } from '@ant-design/icons';
import AuthService from '../../services/authService';

const { Title, Text } = Typography;

const formatDate = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const calculateEndDate = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    d.setDate(d.getDate() + 30);
    return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 23:59까지`;
};

const calculateNextPaymentDate = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    d.setDate(d.getDate() + 30);
    return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
};

export const PaymentSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const paymentKey = searchParams.get('paymentKey');
    const orderId = searchParams.get('orderId');
    const amount = searchParams.get('amount');

    // For billing
    const customerKey = searchParams.get('customerKey');
    const authKey = searchParams.get('authKey');

    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState(null);
    const hasProcessed = React.useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const confirmPayment = async () => {
            try {
                if (paymentKey && orderId && amount) {
                    // 단건 결제 승인 요청
                    const response = await AuthService.authAxios.post('/payments/confirm', {
                        paymentKey,
                        orderId,
                        amount: Number(amount),
                    });
                    setResult(response.data);
                } else if (customerKey && authKey) {
                    // 정기 결제(빌링키) 등록 요청
                    const response = await AuthService.authAxios.post('/payments/billing/auth', {
                        customerKey,
                        authKey,
                    });

                    // 빌링키 등록 성공시 즉시 1개월치 결제 실행(선택사항)
                    if (response.data.success) {
                        const execResponse = await AuthService.authAxios.post(
                            '/payments/billing/execute'
                        );
                        setResult({
                            ...execResponse.data,
                            message: '정기결제 카드가 등록되고 첫 달 요금이 결제되었습니다.',
                        });
                    }
                } else {
                    throw new Error('유효하지 않은 결제 정보입니다.');
                }

                // 등급 및 만료일이 갱신되었으므로 서버로부터 최신 토큰/유저 정보를 동기화
                try {
                    await AuthService.refreshAccessToken();
                    window.dispatchEvent(new Event('user_info_updated'));
                } catch (refreshErr) {
                    console.error('결제 후 정보 동기화 실패, 로컬 정보 임시 업데이트:', refreshErr);
                    const currentUser = AuthService.getUserInfo();
                    if (currentUser) {
                        currentUser.grade = 'premium';
                        const expiresAt = new Date();
                        expiresAt.setDate(expiresAt.getDate() + 30);
                        currentUser.subscription_end_date = expiresAt.toISOString();
                        localStorage.setItem('user_info', JSON.stringify(currentUser));
                        window.dispatchEvent(new Event('user_info_updated'));
                    }
                }
            } catch (error) {
                console.error('Payment confirmation failed:', error);
                setResult({
                    success: false,
                    message:
                        error.response?.data?.message ||
                        error.message ||
                        '결제 승인 중 오류가 발생했습니다.',
                });
            } finally {
                setLoading(false);
            }
        };

        confirmPayment();
    }, [paymentKey, orderId, amount, customerKey, authKey]);

    if (loading) {
        return (
            <div style={{ padding: '80px 20px', textAlign: 'center' }}>
                <Spin size="large" />
                <Title level={4} style={{ marginTop: '24px' }}>
                    결제를 승인하고 있습니다...
                </Title>
                <Text type="secondary">창을 닫지 마시고 잠시만 기다려주세요.</Text>
            </div>
        );
    }

    if (!result?.success) {
        return (
            <div style={{ padding: '80px 20px', textAlign: 'center' }}>
                <Result
                    status="error"
                    title="결제 승인 실패"
                    subTitle={
                        result?.message ||
                        '결제 처리 중 문제가 발생했습니다. 고객센터로 문의해주세요.'
                    }
                    extra={[
                        <Button
                            type="primary"
                            key="console"
                            onClick={() => navigate('/grade-guide')}
                        >
                            요금제 안내로 돌아가기
                        </Button>,
                    ]}
                />
            </div>
        );
    }

    return (
        <div style={{ padding: '80px 20px', maxWidth: '600px', margin: '0 auto' }}>
            <Card
                style={{
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                    textAlign: 'center',
                }}
            >
                <CheckCircleFilled
                    style={{ fontSize: '64px', color: '#52c41a', marginBottom: '24px' }}
                />
                <Title level={3} style={{ marginBottom: '8px' }}>
                    결제가 완료되었습니다!
                </Title>
                <Text
                    style={{
                        fontSize: '16px',
                        color: '#595959',
                        display: 'block',
                        marginBottom: '32px',
                    }}
                >
                    이제 Premium 등급의 모든 기능을 제한 없이 사용하실 수 있습니다.
                </Text>

                <Descriptions
                    bordered
                    column={1}
                    size="small"
                    style={{ textAlign: 'left', marginBottom: '32px' }}
                    labelStyle={{ width: '120px', background: '#fafafa' }}
                >
                    <Descriptions.Item label="결제 금액">
                        <Text strong style={{ color: '#1890ff' }}>
                            {Number(result?.data?.totalAmount || amount || 50000).toLocaleString()}원
                        </Text>
                        {result?.billingKey && (
                            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                                (매월 정기결제)
                            </Text>
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="이용 기간">
                        {calculateEndDate(result?.data?.approvedAt || new Date().toISOString())}
                        <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                            (30일)
                        </Text>
                    </Descriptions.Item>
                    {result?.billingKey && (
                        <Descriptions.Item label="다음 결제일">
                            <Text strong style={{ color: '#fa8c16' }}>
                                {calculateNextPaymentDate(
                                    result?.data?.approvedAt || new Date().toISOString()
                                )}
                            </Text>
                            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                                (등록된 카드로 자동 결제)
                            </Text>
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label="결제 일시">
                        {formatDate(result?.data?.approvedAt || new Date().toISOString())}
                    </Descriptions.Item>
                    <Descriptions.Item label="결제 수단">
                        {result?.data?.method || '카드'}
                        {result?.data?.card?.company && ` (${result?.data?.card?.company})`}
                    </Descriptions.Item>
                    <Descriptions.Item label="승인 번호">
                        {result?.billingKey
                            ? result?.data?.card?.approveNo && result.data.card.approveNo !== '00000000'
                                ? result.data.card.approveNo
                                : result?.data?.paymentKey || '정기결제 승인 완료'
                            : result?.data?.card?.approveNo || result?.data?.paymentKey || '승인 완료'}
                    </Descriptions.Item>
                    <Descriptions.Item label="주문 번호">
                        {orderId || '정기결제 자동생성'}
                    </Descriptions.Item>
                </Descriptions>

                <div style={{ display: 'flex', gap: '12px' }}>
                    {result?.data?.receipt?.url && (
                        <Button
                            size="large"
                            icon={<FileTextOutlined />}
                            onClick={() => window.open(result.data.receipt.url, '_blank')}
                            style={{ flex: 1, height: '48px', borderRadius: '8px' }}
                        >
                            매출전표 (영수증)
                        </Button>
                    )}
                    <Button
                        type="primary"
                        size="large"
                        onClick={() => (window.location.href = '/payment/history')}
                        style={{
                            flex: 2,
                            height: '56px',
                            fontSize: '18px',
                            borderRadius: '8px',
                            fontWeight: 'bold',
                        }}
                    >
                        결제/구독 관리 내역 보기
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default PaymentSuccess;
